import { useParams } from 'react-router-dom';
import { LoadingSpinner } from '@Auth/components/LoadingSpinner';
import type { ButtonProps } from '@components/Button';
import Form from '@components/Form';
import { Stepper } from '@components/Stepper';
import { ResourceState } from '@domain/schemas/contract.schema';
import { Step, useStepper } from '@features/create-entity-modal';
import { ErrorController } from '@features/error';
import { preventDefaultEnterSubmit } from '@helpers/preventDefaultEnterSubmit';
import { useUserTeams } from '@hooks/index';
import { fetchEntity } from '@queries/fetchEntity';
import { useQuery } from '@tanstack/react-query';
import { withPPLGuard } from '@utils/ppl-guard';

import { FormFooter } from '../components/FormFooter';
import { useCreateContract } from '../hooks/useCreateContract';
import { useCreateContractForm } from '../hooks/useCreateContractForm';
import { FormContent } from '../views/FormContent';

const PreDraftContractControllerInner = () => {
  const { id } = useParams();

  const { currentStep, getStepperItemState, onNext, onPrevious } = useStepper();
  const { isLoading, onSubmit } = useCreateContract(id as string);

  const {
    data: contract,
    error,
    isFetching: isFetchingContract,
  } = useQuery({
    // Intentionally separate from entityQueries.detail — uses dataRange:[] (no sub-resources)
    // to keep the pre-draft form fetch lightweight. Sharing entityQueries key would
    // poison the full entity cache with partial data.
    queryKey: ['contract-pre-draft', id] as const,
    queryFn: () => fetchEntity({ entityId: id as string, dataRange: [] }),
  });

  const { data: userTeams = [], isFetching: isFetchingTeams } = useUserTeams();
  const { control, formValues, handleSubmit, isValid, setError, setValue } = useCreateContractForm(
    currentStep,
    contract,
  );

  const commonButtonProps: ButtonProps = { isDisabled: !isValid, className: 'h-9.5', kind: 'primary' };

  const isLastStep = currentStep === Step.BrandingInfo;

  if (isFetchingTeams || isFetchingContract) {
    return <LoadingSpinner />;
  }

  if (error || (contract && contract.status !== ResourceState.PRE_DRAFT)) {
    return <ErrorController type="unknown" />;
  }

  return (
    <div className="mx-auto flex w-[30rem] flex-col items-center gap-3 pt-32">
      <h1 className="text-info-900 text-4xl font-bold">Create a new contract</h1>
      <p className="text-info-500 text-base font-normal">
        Please fill in the following information to set up a contract
      </p>
      <div className="py-4">
        <Stepper getStepperItemState={getStepperItemState} />
      </div>
      <Form
        className="flex w-full flex-col gap-6 pb-6"
        onSubmit={handleSubmit(onSubmit)}
        aria-label="Create contract form"
        onKeyDown={preventDefaultEnterSubmit}
      >
        <FormContent
          contract={contract}
          control={control}
          currentStep={currentStep}
          formValues={formValues}
          setError={setError}
          setValue={setValue}
          userTeams={userTeams}
          isFetchingTeams={isFetchingTeams}
        />

        <FormFooter
          {...commonButtonProps}
          currentStep={currentStep}
          isLastStep={isLastStep}
          isLoading={isLoading}
          onNext={onNext}
          onPrevious={onPrevious}
        />
      </Form>
    </div>
  );
};

export const PreDraftContractController = withPPLGuard(PreDraftContractControllerInner);
